// src/components/patient/PatientBanner.tsx
// The persistent identity band for the visit pipeline — generalised from
// features/ip-case-sheet/PatientHeader.tsx (the IP module's own version of
// this exact idea) and mounted once in VisitWorkflow.tsx so every one of the
// ten OPD stages inherits it, rather than each stage rendering identity
// however it likes (or not at all — see docs/HMS_V4_UI_MODERNIZATION_ASSESSMENT.md
// backlog item 1).
//
// Deliberately reads only data already on the wire: patient/visit fields
// GET /api/visits/:visitId already returns, plus useAllergyStatus (already
// used at Consultation). No new endpoint, no new column.
//
// Not doubly-sticky with VisitStepper on purpose — two independently sticky
// elements need a hand-measured top offset to avoid overlapping, and that
// number would drift every time the stepper's own height changes (compact
// vs full, phone vs desktop). This renders as a normal block directly under
// the (already sticky) stepper instead: still visible without scrolling on
// first paint, without the CSS fragility.
import { useState, type ReactNode } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { Badge } from '@/components/ui/Badge';
import { severityClass } from '@/components/ui/AlertStrip';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/Dialog';
import { buildAllergyAlerts } from '@/features/consultation/allergyAlerts';
import { AllergyPanel } from '@/features/consultation/AllergyPanel';
import { useAllergies } from '@/features/consultation/useAllergies';
import { useAllergyDigest } from '@/features/consultation/useAllergyStatus';
import type { Patient, Visit, QueueEntryRow } from '@/types/visit';
import { patientDisplayName } from '@/lib/patientName';

export interface PatientBannerProps {
  patient: Patient | null;
  visit: Visit;
  /** Human label for the visit's current stage, e.g. "Pre-Consult" — the
   * banner shows position in the pipeline, not just who the patient is. */
  stageLabel?: string;
  queueEntry?: QueueEntryRow | null;
  /** Count of screenings currently due, when the active stage knows one
   * (Pre-Consult computes it live as the checklist is filled). Undefined
   * means "this stage has nothing to say about screenings", which is not the
   * same as zero and renders nothing. */
  screeningsDue?: number;
  /** Opens the full patient record. Threaded from VisitWorkflow's own
   * `onViewPatientRecord`; the link is omitted when no handler is supplied. */
  onViewPatientRecord?: (uhid: string) => void;
  /** Stage-specific badges beyond the ones above — the banner itself stays
   * stage-agnostic; callers add what's relevant to their own screen here
   * rather than this component growing a prop per stage. */
  extra?: ReactNode;
}

/**
 * The one allergy surface on this screen (2026-09-17 redesign, Phase 1.1) —
 * replaces the separate `AlertStrip` that used to sit under this banner in
 * ConsultStage. The badge and its "Why?" popover both read
 * `buildAllergyAlerts()` off the same `useAllergies` rows the digest above is
 * derived from, so the two can never disagree about what's on record.
 */
function AllergyBadge({ uhid }: { uhid: string }) {
  const { status, substances } = useAllergyDigest(uhid);
  // Same query key as useAllergyDigest's own internal useAllergies(uhid) —
  // react-query dedupes this, so it isn't a second request.
  const allergiesQuery = useAllergies(uhid);
  const [manageOpen, setManageOpen] = useState(false);

  if (status === null) return null; // loading/erroring/no patient yet — say nothing rather than guess

  const alerts = buildAllergyAlerts(
    allergiesQuery.data,
    !allergiesQuery.isLoading && !allergiesQuery.isError
  );

  const badge = status === 'danger'
    // Name the allergen — the point of a prescriber-facing badge. Past two,
    // the list would crowd out everything else on the line, so it degrades to
    // a count and the full list stays one click away in the record.
    ? <Badge variant="danger">{`⚠ Allergy: ${substances.slice(0, 2).join(', ')}${substances.length > 2 ? ` +${substances.length - 2} more` : ''}`}</Badge>
    : status === 'warn'
      ? <Badge variant="warn">Allergies not reviewed</Badge>
      : <Badge variant="success">No known allergies</Badge>;

  if (!alerts.length) return badge; // reviewed and clear — nothing to explain

  return (
    <>
      <Popover.Root>
        <Popover.Trigger asChild>
          <button type="button" className="rounded-full outline-none focus-visible:ring-2 focus-visible:ring-teal">
            {badge}
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            side="bottom"
            align="start"
            sideOffset={6}
            className="z-[150] w-80 rounded-[var(--radius)] border border-line bg-card p-3 shadow-lg"
          >
            <div className="flex flex-col gap-2.5">
              {alerts.map(alert => (
                <div key={alert.key} className={`rounded-lg border px-3 py-2 ${severityClass(alert.severity)}`}>
                  <div className="text-[13px] font-semibold">{alert.message}</div>
                  {alert.detail && <div className="mt-0.5 text-[11.5px] opacity-90">{alert.detail}</div>}
                  {alert.basis && (
                    <div className="mt-1.5 rounded border border-current/20 bg-card/60 px-2 py-1.5 text-[11px] opacity-90">
                      <span className="font-semibold">Basis: </span>{alert.basis}
                      <div className="mt-1 opacity-70">{alert.advisoryNote}</div>
                    </div>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={() => setManageOpen(true)}
                className="self-start text-[12px] font-semibold text-teal underline-offset-2 hover:underline"
              >
                Manage allergies
              </button>
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>

      <Dialog open={manageOpen} onOpenChange={setManageOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Allergies</DialogTitle>
          </DialogHeader>
          <AllergyPanel uhid={uhid} />
        </DialogContent>
      </Dialog>
    </>
  );
}

/** `last_visit_at` is the previous visit's start, which on a returning
 * patient is the visit *before* this one — but on the very first visit it is
 * this one, and "Last visit: today" is noise. Suppressed when it falls on the
 * same day as the visit on screen. */
function lastVisitLabel(patient: Patient, visit: Visit): string | null {
  if (!patient.last_visit_at) return null;
  const last = new Date(patient.last_visit_at);
  if (Number.isNaN(last.getTime())) return null;
  const started = visit.started_at ? new Date(visit.started_at) : null;
  if (started && !Number.isNaN(started.getTime()) && last.toDateString() === started.toDateString()) return null;
  return `Last visit ${last.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}`;
}

export function PatientBanner({ patient, visit, stageLabel, queueEntry, screeningsDue, onViewPatientRecord, extra }: PatientBannerProps) {
  if (!patient?.uhid) return null; // nothing to show before identity is known (Verify/early Register)

  return (
    <div className="border-b border-line bg-card px-4 py-2.5 sm:px-6">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <span className="truncate text-[15px] font-semibold text-ink">{patientDisplayName(patient) || 'Unnamed patient'}</span>
            <span className="text-xs text-ink-muted">{patient.uhid}</span>
            <span className="text-xs text-ink-muted">
              {patient.age != null ? `${patient.age}y` : '—'} · {patient.gender || '—'}
              {patient.blood_group ? ` · ${patient.blood_group}` : ''}
            </span>
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11.5px] text-ink-muted">
            {stageLabel && <span>{stageLabel}</span>}
            {queueEntry?.display_token && <span>Token #{queueEntry.display_token}</span>}
            {visit.assigned_doctor_name && <span>Dr. {visit.assigned_doctor_name}</span>}
            {lastVisitLabel(patient, visit) && <span>{lastVisitLabel(patient, visit)}</span>}
            {visit.started_at && (
              <span>Started {new Date(visit.started_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</span>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-1.5">
          <AllergyBadge uhid={patient.uhid} />
          {screeningsDue != null && screeningsDue > 0 && (
            <Badge variant="warn">{screeningsDue} screening{screeningsDue === 1 ? '' : 's'} due</Badge>
          )}
          {extra}
          {onViewPatientRecord && (
            <button
              type="button"
              onClick={() => onViewPatientRecord(patient.uhid!)}
              className="rounded-full border border-line px-2.5 py-[2px] text-[11px] font-semibold text-ink-muted hover:border-teal hover:text-teal"
            >
              Patient record ↗
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
